import React, { useState, useEffect, useRef } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase';
import { X, Upload, Loader2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useForm } from 'react-hook-form';
import { toast } from '../../components/ui/use-toast';
import { triggerKeywordGeneration } from '../../services/keywordService';
import slugify from '../../utils/slugify';
import { formatPhoneNumber, isValidPhoneNumber } from '../../utils/formatters';
import { compressImage, AVATAR_PRESET, validateImageFile, formatFileSize } from '../../utils/imageCompression';

// Form data interface for creating a mockup/unclaimed trader
interface MockupTraderFormData {
  store_name: string;
  owner_name: string;
  email: string;
  phone_number: string;
  location: string;
  description: string;
  profession_id: number;
  business_type_id: string;
  website?: string;
}

interface AddMockupTraderFormProps {
  onClose: () => void;
  onSave: (newTrader: any) => void;
  isModal?: boolean; // If true, renders as modal overlay. If false, renders inline.
}

/**
 * Admin-only form for adding mockup/unclaimed businesses.
 * These businesses have mockup=true and no user_id, allowing real owners to claim them later.
 */
const AddMockupTraderForm: React.FC<AddMockupTraderFormProps> = ({ onClose, onSave, isModal = true }) => {
  const [loading, setLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);
  const [error, setError] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const avatarPreviewObjectUrlRef = useRef<string | null>(null);
  const [professions, setProfessions] = useState<{ id: number; name: string; category?: string }[]>([]);
  const [businessTypes, setBusinessTypes] = useState<{ id: string; name: string }[]>([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>('');
  const [selectedBusinessTypeName, setSelectedBusinessTypeName] = useState<string>('');
  // Trade categories for tradesman (Type A categories matching registration flow)
  const traderCategories = [
    'Building & Construction',
    'Interiors & Renovation',
    'Plumbing & Heating',
    'Home Maintenance',
    'Garden & Outdoors',
    'Cleaning & Logistics',
    'Motors',
    'Health & Lifestyle',
    'Technology & IT',
    'Creative & Media',
  ];
  const [selectedTraderCategory, setSelectedTraderCategory] = useState<string>('');
  const [isCompressingAvatar, setIsCompressingAvatar] = useState(false);
  const [avatarCompressionInfo, setAvatarCompressionInfo] = useState<string | null>(null);

  // Load business types and professions
  useEffect(() => {
    const fetchData = async () => {
      setTypesLoading(true);
      try {
        // Fetch business types
        const { data: typesData, error: typesError } = await supabaseAdmin
          .from('business_types')
          .select('id, name')
          .order('name');

        if (typesError) throw typesError;
        setBusinessTypes(typesData || []);

        // Don't set default - let user choose business type first
      } catch (err) {
        console.error('Error fetching business types:', err);
        setError('Failed to load business types. Please refresh the page.');
      }
      setTypesLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      if (avatarPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(avatarPreviewObjectUrlRef.current);
        avatarPreviewObjectUrlRef.current = null;
      }
    };
  }, []);

  // Load professions/categories based on selected business type and trade category
  useEffect(() => {
    const fetchProfessions = async () => {
      // Reset professions when business type changes
      if (!selectedBusinessType || businessTypes.length === 0) {
        setProfessions([]);
        return;
      }

      try {
        const businessType = businessTypes.find(bt => bt.id === selectedBusinessType);
        const businessTypeName = businessType?.name?.toLowerCase() || '';
        
        // Update business type name state
        setSelectedBusinessTypeName(businessTypeName);
        
        console.log('Fetching professions for business type:', businessTypeName);

        let tableName = '';
        if (businessTypeName === 'tradesman') {
          tableName = 'professions';
          // For tradesman, require a trade category to be selected first
          if (!selectedTraderCategory) {
            console.log('Tradesman selected but no trade category yet');
            setProfessions([]);
            return;
          }
        } else if (businessTypeName === 'craftsman') {
          tableName = 'craft_professions';
        } else if (businessTypeName === 'hospitality') {
          tableName = 'hospitality_categories';
        } else if (businessTypeName && businessTypeName.includes('professional')) {
          tableName = 'professionals_categories';
        } else {
          console.log('Unknown business type:', businessTypeName);
          setProfessions([]);
          return;
        }

        console.log('Fetching from table:', tableName);

        // Build query with optional category filter for tradesman
        let query = supabaseAdmin
          .from(tableName)
          .select('id, name, category')
          .order('name');

        // Filter by trade category for tradesman
        if (tableName === 'professions' && selectedTraderCategory) {
          query = query.eq('category', selectedTraderCategory);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching professions:', error);
          setProfessions([]);
          return;
        }

        console.log('Fetched professions:', data?.length, 'items');
        setProfessions(data || []);
      } catch (err) {
        console.error('Failed to fetch professions:', err);
        setProfessions([]);
      }
    };

    fetchProfessions();
  }, [selectedBusinessType, businessTypes, selectedTraderCategory]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<MockupTraderFormData>({
    defaultValues: {
      store_name: '',
      owner_name: '',
      email: '',
      phone_number: '',
      location: '',
      description: '',
      profession_id: 0,
      business_type_id: '',
      website: ''
    },
    mode: 'onChange'
  });

  // Generate a URL-friendly slug from store name (basic helper used for placeholders)
  const generateSlug = (storeName: string): string => {
    return storeName
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Ensure the form cannot spin forever if a network request hangs or a remote service is slow.
  const withTimeout = async <T,>(promise: PromiseLike<T>, timeoutMs: number, label: string): Promise<T> => {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`));
      }, timeoutMs);
    });

    try {
      return await Promise.race([Promise.resolve(promise), timeoutPromise]);
    } finally {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    }
  };

  const formatDbErrorMessage = (err: unknown): string => {
    if (err instanceof Error) return err.message;
    const anyErr = err as any;
    const message = String(anyErr?.message ?? anyErr?.error_description ?? 'Database error');
    const code = anyErr?.code ? String(anyErr.code) : '';
    const details = anyErr?.details ? String(anyErr.details) : '';
    const hint = anyErr?.hint ? String(anyErr.hint) : '';
    const constraint = anyErr?.constraint ? String(anyErr.constraint) : '';

    return [
      code ? `${message} (code: ${code})` : message,
      details ? `details: ${details}` : '',
      hint ? `hint: ${hint}` : '',
      constraint ? `constraint: ${constraint}` : '',
    ]
      .filter(Boolean)
      .join(' | ');
  };

  const isUniqueConstraintError = (err: unknown): boolean => {
    const anyErr = err as any;
    const code = anyErr?.code;
    const message = String(anyErr?.message ?? '');
    const details = String(anyErr?.details ?? '');
    const constraint = String(anyErr?.constraint ?? '');
    const haystack = `${message} ${details} ${constraint}`.toLowerCase();
    return (
      (code === '23505' ||
        haystack.includes('duplicate key') ||
        haystack.includes('unique constraint')) &&
      haystack.includes('slug')
    );
  };

  const insertWithSlugRetry = async <T,>(
    tableName: string,
    insertBase: Record<string, any>,
    slugBase: string,
    timeoutMs: number
  ): Promise<T> => {
    const base = slugBase || 'business';
    const maxAttempts = 25;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const candidate = attempt === 0 ? base : `${base}-${attempt + 1}`;
      const insertData = {
        ...insertBase,
        slug: candidate,
      };

      try {
        const insertResult = await withTimeout<any>(
          supabaseAdmin
            .from(tableName)
            .insert([insertData])
            .select()
            .single(),
          timeoutMs,
          `Database insert (${tableName})`
        );

        const { data: newBusiness, error: insertError } = insertResult;

        if (insertError) {
          throw insertError;
        }

        if (!newBusiness) {
          throw new Error('No data returned after insert');
        }

        return newBusiness as T;
      } catch (err) {
        if (isUniqueConstraintError(err)) {
          continue;
        }

        console.error('Insert failed:', {
          tableName,
          slug: candidate,
          attempt: attempt + 1,
          error: err,
          errorMessage: formatDbErrorMessage(err),
        });
        throw new Error(formatDbErrorMessage(err));
      }
    }

    const finalCandidate = `${base}-${Date.now()}`;
    const finalResult = await withTimeout<any>(
      supabaseAdmin
        .from(tableName)
        .insert([
          {
            ...insertBase,
            slug: finalCandidate,
          },
        ])
        .select()
        .single(),
      timeoutMs,
      `Database insert (${tableName})`
    );
    const { data: finalBusiness, error: finalError } = finalResult;

    if (finalError) {
      throw finalError;
    }

    if (!finalBusiness) {
      throw new Error('No data returned after insert');
    }

    return finalBusiness as T;
  };

  const compressAvatarSafely = async (file: File): Promise<File> => {
    // Avoid the UI getting stuck if browser-image-compression (web worker) hangs on certain devices/files.
    // If compression fails or exceeds the timeout, fall back to the original file.
    const compressionTimeoutMs = 15000;

    try {
      return await withTimeout(compressImage(file, AVATAR_PRESET), compressionTimeoutMs, 'Image optimization');
    } catch (err) {
      console.warn('Avatar image optimization failed or timed out; falling back to original file:', err);
      try {
        return await withTimeout(
          compressImage(file, {
            ...AVATAR_PRESET,
            useWebWorker: false,
          }),
          compressionTimeoutMs,
          'Image optimization (no worker)'
        );
      } catch (err2) {
        console.warn('Avatar image optimization (no worker) failed; using original file:', err2);
        return file;
      }
    }
  };

  const onSubmit = async (data: MockupTraderFormData) => {
    if (loading || isSubmitting) return;

    // Validate required fields before triggering loading state
    if (!data.store_name?.trim()) {
      setError('Store name is required');
      return;
    }

    if (!selectedBusinessType) {
      setError('Business type is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('Creating mockup trader:', data);

      let avatarUrl = null;

      // Generate a simple slug base for placeholders
      const baseSlug = generateSlug(data.store_name);

      // Use a placeholder email when none is provided to satisfy NOT NULL/UNIQUE constraints.
      // This will be replaced with the real owner email during the claim process.
      const rawEmail = data.email?.trim();
      const placeholderEmail = `${baseSlug || 'unclaimed-business'}-${uuidv4()}@unclaimed.ballymarket.local`;

      // Shared fields for all business tables
      // Note: user_id must be null for mockup businesses due to foreign key constraints
      // Features and socials will be managed differently for mockup entries
      const baseData = {
        store_name: data.store_name.trim(),
        owner_name: data.owner_name.trim(),
        email: rawEmail || placeholderEmail,
        phone_number: data.phone_number.trim(),
        location: data.location.trim(),
        description: data.description.trim(),
        profession_id: data.profession_id || null,
        website: data.website?.trim() || null,
        avatar_url: avatarUrl,
        verified: false,
        mockup: true,
        user_id: null, // Must be null due to FK constraint - features managed by business_id
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const businessType = businessTypes.find(bt => bt.id === selectedBusinessType);
      const businessTypeName = businessType?.name?.toLowerCase() || '';

      if (businessTypeName === 'tradesman') {
        if (professions.length === 0) {
          setError('No professions are available for the selected trade category. Please add at least one profession before creating a tradesman business.');
          setLoading(false);
          return;
        }

        if (!data.profession_id) {
          setError('Please select a profession for this tradesman business before creating it.');
          setLoading(false);
          return;
        }
      }

      let tableName = 'traders';
      let insertData: any = baseData;

      if (businessTypeName === 'craftsman') {
        tableName = 'crafties';
        insertData = {
          ...baseData,
        };
      } else if (businessTypeName === 'hospitality') {
        tableName = 'hospitality';
        insertData = {
          ...baseData,
          business_type_id: selectedBusinessType
        };
      } else if (businessTypeName && businessTypeName.includes('professional')) {
        tableName = 'professionals';
        insertData = {
          ...baseData,
          business_type_id: selectedBusinessType
        };
      } else {
        // Default: tradesman -> traders table
        tableName = 'traders';
        insertData = {
          ...baseData,
          business_type_id: selectedBusinessType
        };
      }

      console.log(`Inserting mockup business into ${tableName}...`);
      const slugBaseForInsert = slugify(data.store_name) || tableName;
      const newBusiness = await insertWithSlugRetry<any>(tableName, insertData, slugBaseForInsert, 65000);
      console.log('Insert completed');

      // Upload avatar after insert so we can store it under the business id
      if (avatarFile) {
        try {
          console.log('Uploading avatar...');
          const uploadedUrl = await withTimeout(uploadAvatar(newBusiness.id, tableName), 90000, 'Avatar upload');
          const { data: updated, error: avatarUpdateError } = await supabaseAdmin
            .from(tableName)
            .update({ avatar_url: uploadedUrl })
            .eq('id', newBusiness.id)
            .select()
            .single();

          if (!avatarUpdateError && updated) {
            newBusiness.avatar_url = updated.avatar_url;
          } else if (avatarUpdateError) {
            console.warn('Avatar URL update failed:', avatarUpdateError);
          }
        } catch (uploadError) {
          console.error('Avatar upload failed:', uploadError);
        }
      }

      console.log('Successfully created mockup business:', { tableName, newBusiness });
      
      // Trigger keyword generation for the new business (fire-and-forget)
      // Map table name to business type for keyword service
      const keywordBusinessType = tableName === 'crafties' ? 'craftie' : 
                                   tableName === 'hospitality' ? 'hospitality' :
                                   tableName === 'professionals' ? 'professional' : 'trader';
      
      // Do not await this call so the form cannot hang if the
      // keyword pipeline is slow or unavailable.
      triggerKeywordGeneration(
        newBusiness.id,
        keywordBusinessType,
        {
          store_name: newBusiness.store_name,
          description: newBusiness.description,
          location: newBusiness.location
        }
      )
        .then(() => {
          console.log('Keyword generation triggered for new business');
        })
        .catch((keywordError) => {
          console.error('Failed to trigger keyword generation:', keywordError);
        });

      // Note: Social links and features cannot be created for mockup businesses
      // because user_social_links and trader_features tables require a valid user_id
      // that references auth.users. These will be available once the business is claimed.
      
      toast({
        title: 'Success',
        description: `Mockup business "${data.store_name}" created successfully in ${businessType?.name || 'Traders'}. Keywords are being generated.`,
      });

      onSave(newBusiness);
      onClose();
    } catch (err) {
      console.error('Error creating mockup trader:', err);
      const message = formatDbErrorMessage(err);
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadAvatar = async (businessId: number, tableName: string): Promise<string> => {
    if (!avatarFile) return '';

    try {
      if (avatarFile.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      if (!avatarFile.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      const fileExt = avatarFile.name.split('.').pop() || 'jpg';
      const fileName = `${uuidv4()}.${fileExt}`;
      const baseDir = tableName === 'crafties' ? 'crafties'
        : tableName === 'hospitality' ? 'hospitality'
        : tableName === 'professionals' ? 'professionals'
        : 'traders';
      const filePath = `${baseDir}/${businessId}/${fileName}`;

      // Use the authenticated client so the upload uses the current user session.
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: true,
          contentType: avatarFile.type
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (err) {
      console.error('Error uploading avatar:', err);
      throw err;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsCompressingAvatar(true);
      setAvatarCompressionInfo(null);
      setError('');

      const validation = validateImageFile(file, 20);
      if (!validation.isValid) {
        setError(validation.error ?? 'Please select a smaller image file.');
        return;
      }

      const originalSize = file.size;
      const compressedFile = await compressAvatarSafely(file);
      const compressedSize = compressedFile.size;
      const reduction =
        originalSize > 0
          ? (((originalSize - compressedSize) / originalSize) * 100).toFixed(0)
          : '0';

      setAvatarFile(compressedFile);
      if (avatarPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(avatarPreviewObjectUrlRef.current);
        avatarPreviewObjectUrlRef.current = null;
      }
      const nextPreviewUrl = URL.createObjectURL(compressedFile);
      avatarPreviewObjectUrlRef.current = nextPreviewUrl;
      setAvatarPreview(nextPreviewUrl);
      setAvatarCompressionInfo(
        `Image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(
          compressedSize
        )} (${reduction}% smaller)`
      );
    } catch (err) {
      console.error('Error processing avatar image:', err);
      setError('Failed to process avatar image. Please try another file.');
    } finally {
      setIsCompressingAvatar(false);
    }
  };

  const handleBusinessTypeChange = (value: string) => {
    setSelectedBusinessType(value);
    setValue('business_type_id', value);
    // Reset profession and trade category when business type changes
    setProfessions([]);
    setValue('profession_id', 0);
    setSelectedTraderCategory('');
    
    // Update business type name for conditional rendering
    const businessType = businessTypes.find(bt => bt.id === value);
    setSelectedBusinessTypeName(businessType?.name?.toLowerCase() || '');
  };

  // Handle trade category change for tradesman
  const handleTraderCategoryChange = (value: string) => {
    setSelectedTraderCategory(value);
    // Reset profession when trade category changes
    setValue('profession_id', 0);
  };

  // Wrapper classes based on modal vs inline mode
  const wrapperClass = isModal
    ? "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    : "";
  const containerClass = isModal
    ? "bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
    : "w-full";

  return (
    <div className={wrapperClass}>
      <div className={containerClass}>
        {/* Header - only show in modal mode */}
        {isModal && (
          <div className="p-6 flex-shrink-0">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Add Unclaimed Business
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Create a business listing that can be claimed by the real owner later
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        )}

        <div className={isModal ? "p-6 pt-0 overflow-y-auto flex-1" : "p-6"}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              {/* Business Type Selection */}
              <div>
                <Label htmlFor="business_type_id">Business Type*</Label>
                <Select
                  onValueChange={handleBusinessTypeChange}
                  value={selectedBusinessType?.toString() || ''}
                  disabled={typesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={typesLoading ? "Loading types..." : "Select business type"} />
                  </SelectTrigger>
                  <SelectContent>
                    {businessTypes.map((type) => (
                      <SelectItem key={type.id} value={String(type.id)}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Trade Category Selection - only for tradesman */}
              {selectedBusinessTypeName === 'tradesman' && (
                <div>
                  <Label htmlFor="trader_category">Trade Category*</Label>
                  <Select
                    onValueChange={handleTraderCategoryChange}
                    value={selectedTraderCategory}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select trade category" />
                    </SelectTrigger>
                    <SelectContent>
                      {traderCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-gray-500">
                    Select a trade category to see available professions
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="store_name">Business Name*</Label>
                <Input
                  id="store_name"
                  {...register('store_name', { 
                    required: 'Business name is required',
                    minLength: { value: 2, message: 'Business name must be at least 2 characters' }
                  })}
                  className={errors.store_name ? 'border-red-500' : ''}
                  placeholder="e.g. John's Plumbing Services"
                />
                {errors.store_name && (
                  <p className="mt-1 text-sm text-red-500">{errors.store_name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="owner_name">Owner Name*</Label>
                <Input
                  id="owner_name"
                  {...register('owner_name', { required: 'Owner name is required' })}
                  className={errors.owner_name ? 'border-red-500' : ''}
                  placeholder="e.g. John Smith"
                />
                {errors.owner_name && (
                  <p className="mt-1 text-sm text-red-500">{errors.owner_name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Email (optional)</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { 
                    validate: (value) =>
                      !value ||
                      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ||
                      'Invalid email address',
                  })}
                  className={errors.email ? 'border-red-500' : ''}
                  placeholder="business@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  You can leave this empty. The owner will provide or confirm their email when claiming the business.
                </p>
              </div>

              <div>
                <Label htmlFor="phone_number">Phone Number*</Label>
                <Input
                  id="phone_number"
                  {...register('phone_number', { 
                    required: 'Phone number is required',
                    validate: (value) => isValidPhoneNumber(value) || 'Please enter a valid phone number',
                    onBlur: (e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setValue('phone_number', formatted);
                    }
                  })}
                  className={errors.phone_number ? 'border-red-500' : ''}
                  placeholder="e.g. 087 123 4567"
                />
                {errors.phone_number && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone_number.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="location">Location*</Label>
                <Input
                  id="location"
                  {...register('location', { required: 'Location is required' })}
                  className={errors.location ? 'border-red-500' : ''}
                  placeholder="e.g. Ballyconnell, Co. Cavan"
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-500">{errors.location.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="website">Website (Optional)</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://example.com"
                  {...register('website', {
                    pattern: {
                      value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                      message: 'Please enter a valid URL'
                    }
                  })}
                  className={errors.website ? 'border-red-500' : ''}
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-500">{errors.website.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description*</Label>
                <Textarea
                  id="description"
                  {...register('description', { required: 'Description is required' })}
                  className={errors.description ? 'border-red-500' : ''}
                  rows={4}
                  placeholder="Describe the business services..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>

              {/* Profession/Category Selection */}
              {professions.length > 0 && (
                <div>
                  <Label htmlFor="profession_id">Category/Profession</Label>
                  <Select
                    onValueChange={value => setValue('profession_id', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {professions.map((profession) => (
                        <SelectItem
                          key={profession.id}
                          value={String(profession.id)}
                        >
                          {profession.name}
                          {profession.category && ` (${profession.category})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Avatar Upload */}
              <div>
                <Label>Business Image (Optional)</Label>
                <div className="flex items-center space-x-4 mt-2">
                  <img
                    src={avatarPreview || `https://api.dicebear.com/7.x/initials/svg?seed=${watch('store_name') || 'Business'}`}
                    alt="Avatar preview"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="cursor-pointer bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 flex items-center space-x-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Upload image</span>
                  </label>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
                {isCompressingAvatar && (
                  <p className="mt-1 text-xs text-gray-500">Optimizing image...</p>
                )}
                {avatarCompressionInfo && !isCompressingAvatar && (
                  <p className="mt-1 text-xs text-gray-600">{avatarCompressionInfo}</p>
                )}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> This creates an unclaimed business listing. 
                The real business owner can claim it later by verifying their email address.
                No user account will be created until the business is claimed.
              </p>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="px-4 py-2"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="px-4 py-2"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </div>
                ) : (
                  <span>Create Unclaimed Business</span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddMockupTraderForm;
